import React from 'react';
import { createPortal } from 'react-dom';
import Icon from './Icon';
import Button from './Button';
import IconButton from './IconButton';
import useDismissable from './useDismissable';
import './Modal.css';

/*
 * Modal — scrim + centered card (header / body / footer).
 *   open              : controls visibility
 *   onClose(reason)   : called on close ('escape' | 'scrim' | 'button')
 *   title, subtitle   : header content (omit for a chromeless dialog)
 *   footer            : footer node (e.g. action buttons)
 *   size              : 'sm' | 'md' | 'lg'  (max-width)
 *   closeOnScrim      : default true
 *   closeOnEsc        : default true
 *   showClose         : header close (x) button, default true
 * Rendered into <body> via a portal for correct stacking.
 */
export default function Modal({
  open,
  onClose,
  title,
  subtitle,
  footer,
  size = 'md',
  closeOnScrim = true,
  closeOnEsc = true,
  showClose = true,
  className = '',
  children,
  labelledBy,
  ...rest
}) {
  const panelRef = useDismissable(open, onClose, { closeOnEsc });
  if (!open) return null;

  const titleId = labelledBy || (title ? 'ui-modal-title' : undefined);

  const onScrimClick = (e) => {
    if (e.target === e.currentTarget && closeOnScrim) onClose && onClose('scrim');
  };

  const hasHead = title != null || showClose;

  return createPortal(
    <div className="ui-modal-scrim open" onMouseDown={onScrimClick}>
      <div
        ref={panelRef}
        className={`ui-modal ui-modal--${size}${className ? ' ' + className : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        {...rest}
      >
        {hasHead && (
          <div className="ui-modal__head">
            <div className="grow">
              {title != null && <h2 id={titleId} className="h-sec">{title}</h2>}
              {subtitle != null && <div className="sub">{subtitle}</div>}
            </div>
            {showClose && (
              <IconButton size="sm" variant="ghost" label="Close" onClick={() => onClose && onClose('button')}>
                <Icon name="close" size={16} />
              </IconButton>
            )}
          </div>
        )}
        <div className="ui-modal__body">{children}</div>
        {footer != null && <div className="ui-modal__foot">{footer}</div>}
      </div>
    </div>,
    document.body
  );
}

/*
 * ConfirmModal — convenience confirm/cancel dialog.
 *   onConfirm() / onCancel()  (onCancel falls back to onClose)
 *   confirmLabel, cancelLabel
 *   danger : renders the confirm button in the danger variant
 *   loading: confirm button shows a spinner + disables
 */
export function ConfirmModal({
  open,
  onClose,
  onConfirm,
  onCancel,
  title = 'Are you sure?',
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  danger = false,
  loading = false,
  children,
  ...rest
}) {
  const cancel = onCancel || (() => onClose && onClose('button'));
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="sm"
      footer={
        <div className="ui-modal__actions">
          <Button variant="default" onClick={cancel} disabled={loading}>{cancelLabel}</Button>
          <Button variant={danger ? 'danger' : 'primary'} onClick={onConfirm} loading={loading}>{confirmLabel}</Button>
        </div>
      }
      {...rest}
    >
      {message != null ? <p className="ui-modal__msg">{message}</p> : children}
    </Modal>
  );
}

/*
 * StatusModal — success / error result dialog with a big icon + message.
 *   status : 'success' | 'error' | 'warning' | 'info'
 *   title, message
 *   actionLabel (default 'Done')
 */
const STATUS_META = {
  success: { icon: 'check',   cls: 'is-success' },
  error:   { icon: 'close',   cls: 'is-error' },
  warning: { icon: 'warning', cls: 'is-warning' },
  info:    { icon: 'warning', cls: 'is-info' },
};

export function StatusModal({
  open,
  onClose,
  status = 'success',
  title,
  message,
  actionLabel = 'Done',
  children,
  ...rest
}) {
  const meta = STATUS_META[status] || STATUS_META.success;
  return (
    <Modal
      open={open}
      onClose={onClose}
      size="sm"
      showClose={false}
      className="ui-modal--status"
      footer={
        <div className="ui-modal__actions ui-modal__actions--center">
          <Button variant="primary" onClick={() => onClose && onClose('button')}>{actionLabel}</Button>
        </div>
      }
      {...rest}
    >
      <div className={`ui-status ${meta.cls}`}>
        <span className="ui-status__icon"><Icon name={meta.icon} size={28} strokeWidth={2.2} /></span>
        {title != null && <div className="ui-status__title">{title}</div>}
        {message != null ? <div className="ui-status__msg">{message}</div> : children}
      </div>
    </Modal>
  );
}
